import { useEffect, useState } from "react";

import { useI18n } from "../i18n/I18nProvider.js";
import type { TranslationKey } from "../i18n/translations.js";
import type { StagedUpdate } from "../offline/database.js";
import {
  activateStagedUpdate,
  discoverUpdate,
  downloadAndVerifyUpdate,
  recoverInterruptedUpdates,
  rollbackUpdate,
} from "../updates/updatePipeline.js";
import type { UpdateFailureReason } from "../updates/updatePipeline.js";

type ViewState =
  "IDLE" | "CHECKING" | "UP_TO_DATE" | "DOWNLOADING" | "VERIFYING" | "ACTIVATED" | "FAILED";

const releaseIndexUrl = import.meta.env.VITE_RELEASE_INDEX_URL as string | undefined;

export function UpdateManager() {
  const { locale, t } = useI18n();
  const [view, setView] = useState<ViewState>("IDLE");
  const [update, setUpdate] = useState<StagedUpdate>();
  const [failure, setFailure] = useState<UpdateFailureReason>();
  const [confirming, setConfirming] = useState<"activate" | "rollback">();

  useEffect(() => {
    void recoverInterruptedUpdates();
  }, []);

  const check = async () => {
    if (releaseIndexUrl === undefined || releaseIndexUrl.length === 0) {
      setFailure("INDEX_UNAVAILABLE");
      setView("FAILED");
      return;
    }
    setView("CHECKING");
    setFailure(undefined);
    const result = await discoverUpdate(locale, releaseIndexUrl);
    if (!result.ok) {
      setFailure(result.reason);
      setView("FAILED");
    } else if (result.status === "UP_TO_DATE") {
      setView("UP_TO_DATE");
    } else {
      setUpdate(result.update);
      setView("IDLE");
    }
  };

  const download = async () => {
    if (update === undefined || releaseIndexUrl === undefined) return;
    setView("DOWNLOADING");
    const result = await downloadAndVerifyUpdate(update.id, releaseIndexUrl);
    if (!result.ok) {
      setFailure(result.reason);
      setView("FAILED");
      return;
    }
    setUpdate(result.update);
    setView("VERIFYING");
  };

  const activate = async () => {
    if (update === undefined) return;
    const result = await activateStagedUpdate(update.id, true);
    setConfirming(undefined);
    if (!result.ok) {
      setFailure(result.reason);
      setView("FAILED");
      return;
    }
    setUpdate(result.update);
    setView("ACTIVATED");
  };

  const rollback = async () => {
    const result = await rollbackUpdate(locale, true);
    setConfirming(undefined);
    if (!result.ok) {
      setFailure(result.reason);
      setView("FAILED");
      return;
    }
    setView("ACTIVATED");
  };

  return (
    <section aria-labelledby="update-manager-title" className="update-manager settings-card">
      <p className="eyebrow">{t("update.eyebrow")}</p>
      <h2 id="update-manager-title">{t("update.title")}</h2>
      <p>{t("update.body")}</p>

      <p
        aria-live="polite"
        className={`update-state update-state--${view.toLowerCase()}`}
        role="status"
      >
        {view === "FAILED" && failure !== undefined
          ? t(`update.reason.${failure}` as TranslationKey)
          : t(`update.state.${view}` as TranslationKey)}
      </p>

      {update !== undefined && (
        <dl className="update-summary">
          <div>
            <dt>{t("update.bundle")}</dt>
            <dd>{update.candidate.bundleId}</dd>
          </div>
          <div>
            <dt>{t("update.version")}</dt>
            <dd>{update.candidate.contentVersion}</dd>
          </div>
          <div>
            <dt>{t("update.locale")}</dt>
            <dd>{update.locale}</dd>
          </div>
          <div>
            <dt>{t("update.key")}</dt>
            <dd>{update.candidate.signingKeyId}</dd>
          </div>
        </dl>
      )}

      <div className="update-actions">
        <button
          className="button button--primary"
          disabled={view === "CHECKING"}
          onClick={() => void check()}
        >
          {t("update.check")}
        </button>
        {update !== undefined && update.status !== "VERIFIED" && update.status !== "ACTIVATED" && (
          <button
            className="button"
            disabled={view === "DOWNLOADING"}
            onClick={() => void download()}
          >
            {view === "FAILED" ? t("update.retry") : t("update.download")}
          </button>
        )}
        {update?.status === "VERIFIED" && confirming !== "activate" && (
          <button className="button" onClick={() => setConfirming("activate")}>
            {t("update.activate")}
          </button>
        )}
        <button className="button" onClick={() => setConfirming("rollback")}>
          {t("update.rollback")}
        </button>
      </div>

      {confirming !== undefined && (
        <div className="update-confirmation" role="group" aria-label={t("update.confirm_title")}>
          <p>
            {t(confirming === "activate" ? "update.confirm_activate" : "update.confirm_rollback")}
          </p>
          <button
            className="button button--primary"
            onClick={() => void (confirming === "activate" ? activate() : rollback())}
          >
            {t("update.confirm")}
          </button>
          <button className="button" onClick={() => setConfirming(undefined)}>
            {t("update.cancel")}
          </button>
        </div>
      )}
    </section>
  );
}
